import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IApp } from '@core/models/app.interfaces';
import { LookupService } from '@core/services/lookup.service';
import { defaultAccountSelected, StoreService } from '@core/store/store.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@shared/components/ng-select/ng-select.module';
import { NgLabelTemplateDirective, NgMultiLabelTemplateDirective, NgOptgroupTemplateDirective } from '@shared/components/ng-select/ng-templates.directive';
import { Subject } from 'rxjs';
import { filter, skip, takeUntil } from 'rxjs/operators';

const DEFAULT_SELECTION = defaultAccountSelected;

@Component({
  selector: 'app-facility-account-chooser',
  templateUrl: './facility-account-chooser.component.html',
  styleUrls: ['./facility-account-chooser.component.scss'],
  imports: [CommonModule, TranslateModule, NgSelectModule, RouterModule, FormsModule,
    NgOptgroupTemplateDirective, NgLabelTemplateDirective, NgMultiLabelTemplateDirective]
})
export class FacilityAccountChooserComponent implements OnInit, OnDestroy {

  private _destroy$ = new Subject<any>();
  public facilityNames: IApp.ILookup[];
  @ViewChild('facilityNameNgSelect', {static: false}) facilityNameNgSelect;

  selectedItems: any;
  show: boolean;

  constructor(
    private storeService: StoreService,
    private lookupService: LookupService,
    private translateService: TranslateService,
  ) {
    this.translateService.onLangChange
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        const facilityNames = this.facilityNames;
        facilityNames[0].name = this.translateService.instant('COMMON.facilityAccountChooser.allAccountsText');
        this.facilityNames = [...facilityNames];
      });
  }

  ngOnInit() {
    this.getFacilityNames();
    this.storeService.accountFacilities$
      .pipe(
        skip(1),
        filter(value => !!value && value.length > 0),
        takeUntil(this._destroy$),
      )
      .subscribe(
        () => this.getFacilityNames(),
      );
  }

  getFacilityNames() {
    this.show = !this.storeService.user.checkIfCFRNonAdmin();
    this.lookupService.getFacilityNames(true).pipe(
      takeUntil(this._destroy$),
    ).subscribe(value => {
      this.facilityNames = [
        {
          id: -1,
          name: this.translateService.instant('COMMON.facilityAccountChooser.allAccountsText'),
        }, ...value];
      setTimeout(() => {
        this.selectedItems = this.storeService.accountFacilities;
        this.reorderFacilityNames();
      }, 100);
    });
  }

  onAdd(item) {
    const processItem = i => {
      this.facilityNameNgSelect.handleClearClick();
      setTimeout(() => {
        const option = this.facilityNameNgSelect.itemsList.items.find(opt => opt.value['id'] === i.id);
        this.facilityNameNgSelect.select(option);
        this.storeService.accountFacilities = this.selectedItems.flat();
      });
    };

    if (item.id === DEFAULT_SELECTION) {
      if (this.selectedItems[0] && this.selectedItems[0] !== DEFAULT_SELECTION) {
        processItem(item);
      }
    } else {
      const firstItem = this.selectedItems[0];
      if (firstItem && firstItem === DEFAULT_SELECTION) {
        processItem(item);
      }
    }
    this.storeService.accountFacilities = this.selectedItems.flat();
    this.reorderFacilityNames();
  }

  onRemove(item) {
    if (this.selectedItems.length === 0) {
      const option = this.facilityNameNgSelect.itemsList.items.find(opt => opt.value['id'] === DEFAULT_SELECTION);
      this.facilityNameNgSelect.select(option);
    }
    this.storeService.accountFacilities = this.selectedItems.flat(); 
    this.reorderFacilityNames();   
  }

  getSelectedAccountNames(items) {
    return items.map(i => i.name).join(', ');
  }

  reorderFacilityNames() {
    const selected = this.facilityNames.filter(facility => this.selectedItems.includes(facility.id));
    this.facilityNames = [...selected, ...this.facilityNames.filter(facility => !this.selectedItems.includes(facility.id))];
  }

  ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

}
